import {getCurrentUserLogin} from '@github-ui/client-env'
import {ReviewRequestsResponseSchema} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {pageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {useQueries} from '@github-ui/react-query'

import type {StackData} from '../../../../page-data/payloads/header'
import {PullRequestState} from '../../../../types'
import {selectReviewRequested} from '../reviewer/use-review-requested-data'
import {useLayoutAuthorLogin} from '../../../../stores/layout-selectors'

export type StackPullRequest = StackData['pulls'][number]

/**
 * Downstack pull requests (closer to the base branch) that the viewer might still need to
 * review, ordered from closest to farthest. `stack.pulls` is ordered head to base, so
 * downstack entries are the ones after the current pull request in the array.
 *
 * A closed downstack pull request must be resolved first (see `StackClosed`), so any closed
 * entry short-circuits this to an empty list rather than skipping past it.
 */
export function selectReviewableDownstackPullRequests(
  stack: StackData | null | undefined,
  pullRequestNumber: number,
): StackPullRequest[] {
  if (!stack) return []

  const currentIndex = stack.pulls.findIndex(pull => pull.number === pullRequestNumber)
  if (currentIndex === -1) return []

  const downstackPullRequests = stack.pulls.slice(currentIndex + 1)
  if (downstackPullRequests.some(pull => pull.state === PullRequestState.Closed)) return []

  return downstackPullRequests.filter(pull => pull.state !== PullRequestState.Merged)
}

export type DownstackReviewRequestsResult = {
  data: StackPullRequest[]
  queryStatus: 'loading' | 'error' | 'success'
}

// Cap how many downstack pull requests we query so a very deep stack can't trigger hundreds of calls.
const MAX_DOWNSTACK_PULL_REQUESTS_TO_QUERY = 10

/**
 * Fetches review-request data for each downstack pull request that isn't merged,
 * and returns every one where the viewer is a requested reviewer, ordered closest to farthest.
 *
 * At most `MAX_DOWNSTACK_PULL_REQUESTS_TO_QUERY` of the closest downstack pull requests are
 * queried; any farther ones are ignored.
 */
export function useDownstackPendingReviewRequests(
  stack: StackData | null | undefined,
  pullRequestNumber: number,
): DownstackReviewRequestsResult {
  const viewerLogin = getCurrentUserLogin()
  const authorLogin = useLayoutAuthorLogin()
  const downstackPullRequests = selectReviewableDownstackPullRequests(stack, pullRequestNumber).slice(
    0,
    MAX_DOWNSTACK_PULL_REQUESTS_TO_QUERY,
  )
  const queryIsEnabled = Boolean(viewerLogin) && authorLogin !== viewerLogin

  // TODO: for private preview/staff ship, querying each pull request individually is fine, but we
  // very likely want to add a new endpoint in the future so we only need to make a single call.
  // https://github.com/github/pull-requests/issues/27273 see this issue
  const results = useQueries({
    queries: downstackPullRequests.map(pull => {
      const apiUrl = pageDataUrl(pull.url, PageData.reviewRequests)
      return {
        queryKey: [PageData.reviewRequests, apiUrl] as const,
        queryFn: () => queryFnFetch({queryDeps: {pathname: apiUrl}}, {schema: ReviewRequestsResponseSchema}),
        enabled: queryIsEnabled,
        staleTime: Infinity,
      }
    }),
  })

  if (!viewerLogin || downstackPullRequests.length === 0) {
    return {data: [], queryStatus: 'success'}
  }

  if (results.some(result => result.isError)) {
    return {data: [], queryStatus: 'error'}
  }

  if (results.some(result => result.isLoading)) {
    return {data: [], queryStatus: 'loading'}
  }

  const pullRequestsAwaitingReview = downstackPullRequests.filter((_pull, index) => {
    const pendingReviewRequests = results[index]?.data?.pendingReviewRequests ?? []
    return selectReviewRequested(pendingReviewRequests, viewerLogin) !== null
  })

  return {data: pullRequestsAwaitingReview, queryStatus: 'success'}
}
